import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Star, Clock, User, Eye, ChevronLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { getApiError } from '../services/api'
import { useAuthStore } from '../stores/authStore'

function VideoaulaPage() {
  const { id, youtubeId, recommendationId, historyId } = useParams()
  const isYoutubeResult = Boolean(youtubeId)
  const isRecommendation = Boolean(recommendationId)
  const isHistory = Boolean(historyId)
  const isTransientVideo = isYoutubeResult || isRecommendation || isHistory
  const videoKey = youtubeId || recommendationId || historyId || id
  const [avaliacao, setAvaliacao] = useState(0)
  const [historico, setHistorico] = useState(null)
  const [historyError, setHistoryError] = useState(false)
  const [materialAction, setMaterialAction] = useState(null)
  const [conteudoBase, setConteudoBase] = useState('')
  const [showSummaryEditor, setShowSummaryEditor] = useState(false)
  const accessToken = useAuthStore(state => state.accessToken)
  const queryClient = useQueryClient()

  const { data: video, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['videoaula', isYoutubeResult ? 'youtube' : isRecommendation ? 'recomendacao' : isHistory ? 'historico' : 'catalogo', videoKey],
    queryFn: async () => {
      const endpoint = isYoutubeResult
        ? `/search/youtube/${youtubeId}`
        : isRecommendation
          ? `/recomendacoes/${recommendationId}`
          : isHistory
            ? `/historico-videoaulas/${historyId}`
          : `/videoaulas/${id}`
      return (await api.get(endpoint)).data.videoaula
    }
  })

  useEffect(() => {
    if (!video || !accessToken || !isTransientVideo) return

    api.post('/historico-videoaulas', {
      youtube_id: video.youtube_id,
      titulo: video.titulo,
      canal: video.canal,
      url: video.url || `https://www.youtube.com/watch?v=${video.youtube_id}`,
      thumbnail_url: video.thumbnail_url,
      descricao: video.descricao_ia,
      duracao_segundos: video.duracao_segundos || 0
    }).then(({ data }) => {
      setHistorico(data.historico)
      setHistoryError(false)
    }).catch(() => setHistoryError(true))
  }, [video, accessToken, isTransientVideo])

  const avaliar = useMutation({
    mutationFn: async nota => (await api.post(`/videoaulas/${id}/avaliar`, { nota })).data,
    onSuccess: data => {
      toast.success('Avaliação registrada!')
      setAvaliacao(0)
      queryClient.setQueryData(['videoaula', 'catalogo', id], current => current ? {
        ...current,
        avaliacao_media: data.avaliacao_media,
        total_avaliacoes: data.total_avaliacoes
      } : current)
    },
    onError: err => toast.error(getApiError(err, 'Não foi possível registrar a avaliação.'))
  })

  const formatDuration = segundos => {
    const minutos = Math.floor((segundos || 0) / 60)
    const resto = (segundos || 0) % 60
    return `${minutos}:${resto.toString().padStart(2, '0')}`
  }

  const formatViews = numero => new Intl.NumberFormat('pt-BR', { notation: 'compact' }).format(numero || 0)

  if (isLoading) return <p className="text-sm text-gray-500">Carregando videoaula...</p>

  if (isError) {
    return (
      <div className="card text-sm">
        <p className="text-red-600">{getApiError(error, 'Não foi possível carregar esta videoaula.')}</p>
        <button type="button" className="btn-secondary mt-3" onClick={() => refetch()}>Tentar novamente</button>
      </div>
    )
  }

  const enviarAvaliacao = nota => {
    if (!accessToken) {
      toast.error('Entre na sua conta para avaliar esta aula.')
      return
    }
    setAvaliacao(nota)
    avaliar.mutate(nota)
  }

  const gerarMaterial = async modo => {
    if (!historico) return
    const texto = conteudoBase.trim()
    if (!texto) {
      toast.error('Escreva ou cole suas anotações antes de continuar.')
      return
    }
    if (modo === 'gratuito' && texto.length < 80) {
      toast.error('O resumo gratuito precisa de pelo menos 80 caracteres.')
      return
    }

    setMaterialAction(modo)
    try {
      const { data } = await api.post(
        `/historico-videoaulas/${historico.id}/material`,
        modo === 'manual'
          ? { modo, conteudo_base: texto, resumo: texto }
          : { modo, conteudo_base: texto }
      )
      setHistorico(data.historico)
      setShowSummaryEditor(false)
      toast.success(modo === 'gratuito' ? 'Resumo gratuito criado!' : 'Seu resumo foi salvo!')
    } catch (requestError) {
      toast.error(getApiError(requestError, 'Não foi possível preparar o resumo.'))
    } finally {
      setMaterialAction(null)
    }
  }

  const editarResumo = () => {
    setConteudoBase(historico?.resumo || video.resumo || '')
    setShowSummaryEditor(true)
  }

  return (
    <div className="-mx-4 space-y-4 sm:mx-0">
      <div className="px-4 sm:px-0">
        <Link to={video.tema_id ? `/tema/${video.tema_id}` : '/explorar'} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black">
          <ChevronLeft size={14} /> Voltar à busca
        </Link>
      </div>

      <div className="aspect-video w-full overflow-hidden bg-black sm:rounded-2xl">
        <iframe
          src={`https://www.youtube.com/embed/${video.youtube_id}`}
          title={video.titulo}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>

      <div className="space-y-4 px-4 sm:px-0 lg:max-w-4xl">
        <div>
          <h1 className="mb-2 text-base font-medium leading-snug lg:text-xl">{video.titulo}</h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <span className="flex items-center gap-1"><User size={12} /> {video.professor}</span>
            <span className="flex items-center gap-1"><Clock size={12} /> {formatDuration(video.duracao_segundos)}</span>
            <span className="flex items-center gap-1"><Eye size={12} /> {formatViews(video.visualizacoes)} visualizações</span>
            {!isTransientVideo && (
              <span className="flex items-center gap-1 text-amber-600"><Star size={12} fill="currentColor" /> {video.avaliacao_media || '—'}</span>
            )}
          </div>
        </div>

        {!isTransientVideo && <div className="card">
          <p className="text-sm font-medium mb-1">Como foi essa aula?</p>
          <p className="text-xs text-gray-500 mb-2">Sua avaliação fica salva na sua conta.</p>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(estrela => (
              <button
                key={estrela}
                type="button"
                disabled={avaliar.isLoading}
                onClick={() => enviarAvaliacao(estrela)}
                className="p-1 transition-transform hover:scale-110 disabled:opacity-50"
                aria-label={`Avaliar com ${estrela} estrela${estrela > 1 ? 's' : ''}`}
              >
                <Star size={24} className={estrela <= avaliacao ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
              </button>
            ))}
          </div>
        </div>}

        {isTransientVideo && (
          <p className="text-xs text-gray-400">O link é transitório; somente seu histórico e material de estudo ficam salvos.</p>
        )}

        {accessToken && isTransientVideo && !historico && !historyError && (
          <p className="card text-sm text-gray-500">Preparando esta aula no seu histórico...</p>
        )}

        {accessToken && historyError && (
          <p className="card border-red-200 text-sm text-red-600">Não foi possível preparar esta aula para o resumo. Recarregue a página e tente novamente.</p>
        )}

        {accessToken && historico && ((!historico.resumo && !video.resumo) || showSummaryEditor) && (
          <div className="card space-y-3">
            <div>
              <h2 className="text-sm font-medium">Resumo gratuito</h2>
              <p className="mt-1 text-xs leading-5 text-gray-500">Este recurso não assiste ao vídeo automaticamente. Faça suas anotações enquanto estuda ou, se preferir, produza um texto com uma IA de sua escolha e cole-o abaixo.</p>
            </div>
            <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2.5 text-xs leading-5 text-blue-800 dark:border-blue-950 dark:bg-blue-950/40 dark:text-blue-200">
              Com o texto pronto, você pode pedir ao aplicativo para selecionar os pontos principais gratuitamente ou salvá-lo exatamente como escreveu. Evite incluir informações pessoais ao utilizar serviços externos de IA.
            </div>
            <textarea
              className="input-field min-h-28 resize-y"
              value={conteudoBase}
              onChange={event => setConteudoBase(event.target.value)}
              placeholder="Cole aqui suas anotações, transcrição ou um resumo produzido por você ou por outra IA"
              maxLength={20000}
            />
            <div className="flex items-center justify-between text-[11px] text-gray-400">
              <span>Para o resumo automático: mínimo de 80 caracteres</span>
              <span>{conteudoBase.length}/20000</span>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <button type="button" onClick={() => gerarMaterial('gratuito')} disabled={Boolean(materialAction) || conteudoBase.trim().length < 80} className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-50">
                {materialAction === 'gratuito' ? 'Criando resumo...' : 'Criar resumo gratuito'}
              </button>
              <button type="button" onClick={() => gerarMaterial('manual')} disabled={Boolean(materialAction) || !conteudoBase.trim()} className="btn-secondary w-full disabled:cursor-not-allowed disabled:opacity-50">
                {materialAction === 'manual' ? 'Salvando...' : 'Salvar como meu resumo'}
              </button>
            </div>
            {showSummaryEditor && <button type="button" onClick={() => setShowSummaryEditor(false)} className="w-full text-xs text-gray-500 hover:text-black dark:hover:text-white">Cancelar edição</button>}
          </div>
        )}

        {(historico?.resumo || video.resumo) && (
          <div className="card">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h2 className="text-sm font-medium">Resumo da aula</h2>
              {accessToken && historico && !showSummaryEditor && <button type="button" onClick={editarResumo} className="text-xs font-medium text-gray-500 hover:text-black dark:hover:text-white">Editar</button>}
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{historico?.resumo || video.resumo}</p>
          </div>
        )}

        {(historico?.exercicios?.length > 0 || video.exercicios?.length > 0) && (
          <div className="card">
            <h2 className="text-sm font-medium mb-3">Exercícios de fixação</h2>
            <div className="space-y-3">
              {(historico?.exercicios?.length ? historico.exercicios : video.exercicios).map((exercicio, index) => (
                <details key={`${index}-${exercicio.enunciado}`} className="border-b border-gray-100 pb-3 last:border-0">
                  <summary className="text-sm font-medium cursor-pointer">{index + 1}. {exercicio.enunciado}</summary>
                  {exercicio.resposta && <p className="text-sm text-gray-600 mt-2"><strong>Resposta:</strong> {exercicio.resposta}</p>}
                  {exercicio.explicacao && <p className="text-xs text-gray-500 mt-1">{exercicio.explicacao}</p>}
                </details>
              ))}
            </div>
          </div>
        )}

        {video.descricao_ia && (
          <div className="card">
            <p className="text-sm text-gray-600 leading-relaxed">{video.descricao_ia}</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default VideoaulaPage
